import React from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { API_BASE_URL, authFetch } from '../config/api';

const baseUrl = API_BASE_URL;

interface IngestionJob {
  id: number;
  source_name: string;
  source_path: string;
  status: string;
  result: {
    title?: string;
    content_type?: string;
    hazards?: string[];
    proposals_count?: number;
    error?: string;
  } | null;
  created_at: string;
  updated_at: string;
}

async function fetchJobs(): Promise<IngestionJob[]> {
  const res = await authFetch(`${baseUrl}/api/knowledge/jobs`, { cache: 'no-store' });
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json() as { ok: boolean; jobs: IngestionJob[] };
  return data.jobs;
}

async function retryJob(id: number): Promise<void> {
  const res = await authFetch(`${baseUrl}/api/knowledge/jobs/${id}/retry`, {
    method: 'PUT',
  });
  if (!res.ok) {
    const data = await res.json();
    throw new Error(data.error || 'Retry failed');
  }
}

async function clearFinishedJobs(): Promise<{ deleted: number }> {
  const res = await authFetch(`${baseUrl}/api/knowledge/jobs/clear`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const data = await res.json();
    throw new Error(data.error || 'Clear failed');
  }
  return res.json();
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'Queued',
  processing: 'Starting...',
  extracting: 'Extracting text',
  classifying: 'Classifying content',
  finding_related: 'Finding related notes',
  generating_proposals: 'Generating proposals',
  completed: 'Completed',
  failed: 'Failed',
};

const ACTIVE_STATUSES = ['processing', 'extracting', 'classifying',
  'finding_related', 'generating_proposals'];

function statusClass(status: string): string {
  if (status === 'completed') return 'job-status--completed';
  if (status === 'failed') return 'job-status--failed';
  if (status === 'pending') return 'job-status--pending';
  return 'job-status--active';
}

/** Is this job in a state where a retry makes sense? */
function isRetryable(status: string): boolean {
  return status === 'failed' || ACTIVE_STATUSES.includes(status);
}

/** Time-ago helper */
function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

interface Props {
  activeJobId: number | null;
}

export default function IngestionStatus({ activeJobId }: Props) {
  const queryClient = useQueryClient();

  // Auto-poll when there's an active job OR any job is in-progress
  const { data: jobs } = useQuery<IngestionJob[], Error>(
    ['ingestionJobs'],
    fetchJobs,
    {
      refetchInterval: (data) => {
        if (activeJobId) return 2000;
        // Also keep polling if any job is in an active state
        const hasActive = data?.some(j =>
          j.status === 'pending' || ACTIVE_STATUSES.includes(j.status)
        );
        return hasActive ? 3000 : false;
      },
    }
  );

  const retryMutation = useMutation(retryJob, {
    onSuccess: () => {
      queryClient.invalidateQueries(['ingestionJobs']);
    },
  });

  const clearMutation = useMutation(clearFinishedJobs, {
    onSuccess: () => {
      queryClient.invalidateQueries(['ingestionJobs']);
    },
  });

  if (!jobs || jobs.length === 0) return null;

  const hasFinished = jobs.some(j => j.status === 'completed' || j.status === 'failed');

  return (
    <div className="ingestion-jobs">
      <div className="ingestion-jobs__header">
        <h4>Ingestion Jobs</h4>
        {hasFinished && (
          <button
            className="ingestion-jobs__clear-btn"
            onClick={() => clearMutation.mutate()}
            disabled={clearMutation.isLoading}
          >
            {clearMutation.isLoading ? 'Clearing...' : 'Clear list'}
          </button>
        )}
      </div>
      <div className="ingestion-jobs__list">
        {jobs.map(job => (
          <div key={job.id} className={`ingestion-job ${statusClass(job.status)}`}>
            <div className="ingestion-job__header">
              <span className="ingestion-job__name">{job.source_name}</span>
              <div className="ingestion-job__meta">
                <span className="ingestion-job__time">{timeAgo(job.updated_at)}</span>
                <span className={`staging-badge ${
                  job.status === 'completed' ? 'staging-badge--approved' :
                  job.status === 'failed' ? 'staging-badge--rejected' :
                  'staging-badge--pending'
                }`}>
                  {STATUS_LABELS[job.status] ?? job.status}
                </span>
              </div>
            </div>

            {/* Progress indicator for active jobs */}
            {ACTIVE_STATUSES.includes(job.status) && (
              <div className="ingestion-job__progress">
                <div className="ingestion-job__progress-bar" />
              </div>
            )}

            {job.result && job.status === 'completed' && (
              <div className="ingestion-job__result">
                <span>{job.result.title}</span>
                <span className="text-muted">
                  {job.result.proposals_count} proposal{job.result.proposals_count !== 1 ? 's' : ''} created
                </span>
              </div>
            )}

            {job.status === 'failed' && (
              <div className="ingestion-job__error">
                {job.result?.error ? (
                  <span className="ingestion-job__error-msg">
                    {job.result.error}
                  </span>
                ) : (
                  <span className="ingestion-job__error-msg">
                    Job failed without error details
                  </span>
                )}
                <button
                  className="ingestion-job__retry-btn"
                  onClick={() => retryMutation.mutate(job.id)}
                  disabled={retryMutation.isLoading}
                >
                  {retryMutation.isLoading ? 'Retrying...' : 'Retry'}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
